import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import googleOAuthService from '../utils/googleOAuthService';
import { GOOGLE_CLIENT_ID } from '../config/googleOAuth';

const GoogleSignIn = ({ onSuccess, onError, buttonText = "Continue with Google", className = "" }) => {
    const navigate = useNavigate();
    const googleButtonRef = useRef(null);

    useEffect(() => {
        // Load Google Identity Services script
        const loadGoogleScript = () => {
            if (window.google) {
                initializeGoogleSignIn();
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = initializeGoogleSignIn;
            document.head.appendChild(script);
        };

        const initializeGoogleSignIn = () => {
            if (window.google && googleButtonRef.current) {
                // Debug: Log the client ID to verify it's loaded
                console.log('Google Client ID:', GOOGLE_CLIENT_ID);
                console.log('Client ID length:', GOOGLE_CLIENT_ID?.length);
                console.log('Client ID format check:', GOOGLE_CLIENT_ID?.includes('.apps.googleusercontent.com'));
                
                // Check if client ID is available
                if (!GOOGLE_CLIENT_ID) {
                    console.error('Google Client ID is not available. Please check your .env file.');
                    return;
                }
                
                // Additional validation
                if (!GOOGLE_CLIENT_ID.includes('.apps.googleusercontent.com')) {
                    console.error('Invalid Google Client ID format. Should end with .apps.googleusercontent.com');
                    return;
                }
                
                console.log('Initializing Google Sign-In with client ID:', GOOGLE_CLIENT_ID);
                
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleGoogleSignIn,
                    auto_select: false,
                    cancel_on_tap_outside: true,
                });

                window.google.accounts.id.renderButton(googleButtonRef.current, {
                    // theme: 'outline',  // White theme
                    size: 'large',
                    width: googleButtonRef.current.offsetWidth, // Match container width
                    type: 'standard',
                    text: 'signin_with',
                    shape: 'pill',  // Curved/rounded button
                    logo_alignment: 'left',
                    locale: 'en_US', // Ensure correct locale
                });
            }
        };

        loadGoogleScript();

        return () => {
            // Cleanup if needed
        };
    }, []);

    const handleGoogleSignIn = async (response) => {
        try {
            if (response.credential) {
                // Send the ID token to our backend
                const backendResponse = await axiosInstance.post('/auth/google-signin', {
                    token: response.credential
                });

                if (backendResponse.data.success) {
                    // Store token and user data
                    localStorage.setItem('token', backendResponse.data.token);
                    localStorage.setItem('user', JSON.stringify({
                        name: backendResponse.data.user.name,
                        email: backendResponse.data.user.email,
                        role: backendResponse.data.user.role,
                        profile_picture: backendResponse.data.user.profile_picture
                    }));

                    // Call success callback if provided
                    if (onSuccess) {
                        onSuccess(backendResponse.data);
                    } else {
                        // Default navigation
                        if (backendResponse.data.user.role === 'admin') {
                            localStorage.setItem('admin_token', backendResponse.data.token);
                            navigate('/admin-dashboard');
                        } else {
                            navigate('/dashboard');
                        }
                    }
                } else {
                    throw new Error(backendResponse.data.message || 'Google sign-in failed');
                }
            }
        } catch (error) {
            console.error('Google sign-in error:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Google sign-in failed';
            
            if (onError) {
                onError(errorMessage);
            } else {
                // Handle error (you might want to show a toast or alert)
                alert(errorMessage);
            }
        }
    };

    const handleManualGoogleSignIn = async () => {
        try {
            // Alternative method using Google OAuth flow
            const redirectUri = `${window.location.origin}/auth/google/callback`;
            const authUrl = googleOAuthService.generateAuthUrl(redirectUri);
            window.location.href = authUrl;
        } catch (error) {
            console.error('Manual Google sign-in error:', error);
            if (onError) {
                onError('Failed to initiate Google sign-in');
            }
        }
    };

    // Add a fallback button in case Google's button doesn't render
    const handleFallbackClick = () => {
        if (typeof handleManualGoogleSignIn === 'function') {
            handleManualGoogleSignIn();
        }
    };

    return (
        <div className={`google-signin-container ${className}`}>
            <div 
                ref={googleButtonRef}
                className="google-signin-button"
                onClick={handleFallbackClick} // Fallback click handler
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: '48px',
                    padding: '2px',
                    borderRadius: '50px', /* Curved edges with 50px radius */
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'white'
                }}
            >
                {/* This content will be replaced by Google's button, but serves as a fallback */}
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    width: '100%',
                    color: '#757575', /* Google's text color */
                    fontFamily: 'Roboto, Arial, sans-serif',
                    fontSize: '15px',
                    fontWeight: '500'
                }}>
                    <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        style={{ marginRight: '12px' }}
                    >
                        <path 
                            fill="#4285F4" 
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path 
                            fill="#34A853" 
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path 
                            fill="#FBBC05" 
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path 
                            fill="#EA4335" 
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                    </svg>
                    {buttonText}
                </div>
            </div>
        </div>
    );
};

export default GoogleSignIn; 